"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawExtension = exports.DrawBehavior = exports.DefaultDrawExtensionParams = void 0;
var tslib_1 = require("tslib");
var lodash_1 = require("lodash");
var array2d_1 = require("../data/array2d");
var color_1 = require("../data/color");
var downsampling_1 = require("../data/downsampling");
var image_1 = require("../data/image");
var extension_1 = require("../extension");
var scales_1 = require("../scales");
var utils_1 = require("../utils");
var DefaultColor = color_1.Color.fromString('#888888');
var DefaultColorProvider = function () { return DefaultColor; };
/** Default parameter values for `DrawExtension` */
exports.DefaultDrawExtensionParams = {
    colorProvider: DefaultColorProvider,
    xGapPixels: 2,
    xGapRelative: 0.1,
    yGapPixels: 2,
    yGapRelative: 0.1,
    minRectSizeForGaps: 2,
};
/** Behavior class for `DrawExtension` (draws heatmap data in the canvas element) */
var DrawBehavior = /** @class */ (function (_super) {
    tslib_1.__extends(DrawBehavior, _super);
    function DrawBehavior() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /** Approximate width of a rectangle in pixels, when showing downsampled data.
         * (higher value means more responsive but lower-resolution visualization) */
        _this.downsamplingPixelsPerRect = 1;
        _this._drawer = (0, utils_1.Refresher)(function () { return _this._draw(); });
        return _this;
    }
    DrawBehavior.prototype.register = function () {
        var _this = this;
        _super.prototype.register.call(this);
        this.subscribe(this.state.events.render, function () {
            var _a;
            if (!_this.state.dom)
                return;
            var ctx = (_a = _this.state.dom.canvas.node()) === null || _a === void 0 ? void 0 : _a.getContext('2d');
            if (ctx) {
                _this.ctx = ctx;
            }
            else {
                throw new Error('Failed to initialize canvas');
            }
            _this.state.dom.svg.on('mouseenter.DrawExtension', function () { return _this.requestDraw(); }); // For cases when the browser erases canvas (bug in some Chrome versions)
        });
        this.subscribe(this.state.events.zoom, function () {
            _this.requestDraw();
        });
        this.subscribe(this.state.events.data, function () {
            _this.downsampler = undefined;
            _this.requestDraw();
        });
    };
    DrawBehavior.prototype.update = function (params) {
        if (params.colorProvider !== this.params.colorProvider) {
            this.downsampler = undefined;
        }
        _super.prototype.update.call(this, params);
        this.requestDraw();
    };
    DrawBehavior.prototype.unregister = function () {
        if (this.state.dom)
            this.state.dom.svg.on('mouseenter.DrawExtension', null);
        _super.prototype.unregister.call(this);
    };
    /** Compute full heatmap image based on current data and colorProvider
     * (i.e. each pixel corresponds to one grid cell) */
    DrawBehavior.prototype.computeFullImage = function () {
        var image = image_1.Image.create(this.state.dataArray.nColumns, this.state.dataArray.nRows);
        for (var iy = 0; iy < this.state.dataArray.nRows; iy++) {
            for (var ix = 0; ix < this.state.dataArray.nColumns; ix++) {
                var datum = array2d_1.Array2D.get(this.state.dataArray, ix, iy);
                if (datum === undefined)
                    continue; // keep transparent black
                var color = this.params.colorProvider(datum, this.state.xDomain.values[ix], this.state.yDomain.values[iy], ix, iy);
                var c = (typeof color === 'string') ? color_1.Color.fromString(color) : color;
                image_1.Image.setColor(image, ix, iy, c);
            }
        }
        return image;
    };
    /** Do not call directly! Call `requestDraw` instead to avoid browser freezing. */
    DrawBehavior.prototype._draw = function () {
        var _a;
        if (!this.state.dom)
            return;
        var xResolution = scales_1.Box.width(this.state.boxes.canvas) / this.downsamplingPixelsPerRect;
        var yResolution = scales_1.Box.height(this.state.boxes.canvas) / this.downsamplingPixelsPerRect;
        (_a = this.downsampler) !== null && _a !== void 0 ? _a : (this.downsampler = downsampling_1.Downsampler.fromImage(this.computeFullImage()));
        var downsampledImage = downsampling_1.Downsampler.getDownsampled(this.downsampler, {
            x: xResolution * scales_1.Box.width(this.state.boxes.wholeWorld) / (scales_1.Box.width(this.state.boxes.visWorld)),
            y: yResolution * scales_1.Box.height(this.state.boxes.wholeWorld) / (scales_1.Box.height(this.state.boxes.visWorld)),
        });
        return this.drawThisImage(downsampledImage, this.state.dataArray.nColumns / downsampledImage.nColumns, this.state.dataArray.nRows / downsampledImage.nRows);
    };
    /** Request that the heatmap be drawn on the canvas. */
    DrawBehavior.prototype.requestDraw = function () {
        this._drawer.requestRefresh();
    };
    /** Return a clean `Image` with the size of the canvas, use cached if available. */
    DrawBehavior.prototype.getCleanCanvasImage = function () {
        if (!this.ctx)
            throw new Error('`getCanvasImage` should only be called after canvas is initialized');
        var w = Math.floor(this.ctx.canvas.width);
        var h = Math.floor(this.ctx.canvas.height);
        if (this._canvasImage && this._canvasImage.nColumns === w && this._canvasImage.nRows === h) {
            image_1.Image.clear(this._canvasImage);
        }
        else {
            this._canvasImage = image_1.Image.create(w, h);
        }
        return this._canvasImage;
    };
    /** Return an `ImageData` with the size of the canvas, use cached if available (not necessarily clean). */
    DrawBehavior.prototype.getCanvasImageData = function () {
        if (!this.ctx)
            throw new Error('`getCanvasImageData` should only be called after canvas is initialized');
        var w = Math.floor(this.ctx.canvas.width);
        var h = Math.floor(this.ctx.canvas.height);
        if (this._canvasImageData && this._canvasImageData.width === w && this._canvasImageData.height === h) {
            return this._canvasImageData;
        }
        else {
            this._canvasImageData = new ImageData(w, h);
            return this._canvasImageData;
        }
    };
    DrawBehavior.prototype.drawThisImage = function (image, xScale, yScale) {
        if (!this.state.dom || !this.ctx)
            return;
        this.resizeCanvas(); // Doing this here rather than in 'resize' event handler, to avoid flickering on resize ;)
        var rectWidth = (0, scales_1.scaleDistance)(this.state.scales.worldToCanvas.x, 1) * xScale;
        var rectHeight = (0, scales_1.scaleDistance)(this.state.scales.worldToCanvas.y, 1) * yScale;
        var showXGaps = scales_1.Box.width(this.state.boxes.canvas) > this.params.minRectSizeForGaps * scales_1.Box.width(this.state.boxes.visWorld);
        var showYGaps = scales_1.Box.height(this.state.boxes.canvas) > this.params.minRectSizeForGaps * scales_1.Box.height(this.state.boxes.visWorld);
        var xHalfGap = showXGaps ? 0.5 * this.getXGap(rectWidth) : 0;
        var yHalfGap = showYGaps ? 0.5 * this.getYGap(rectHeight) : 0;
        var globalOpacity = (showXGaps ? 1 : (1 - this.getXGap(rectWidth) / rectWidth))
            * (showYGaps ? 1 : (1 - this.getYGap(rectHeight) / rectHeight));
        this.state.dom.canvas.style('opacity', globalOpacity); // This compensates for not showing gaps by lowering opacity (when scaled)
        var colFrom = (0, lodash_1.clamp)(Math.floor(this.state.boxes.visWorld.xmin / xScale), 0, image.nColumns);
        var colTo = (0, lodash_1.clamp)(Math.ceil(this.state.boxes.visWorld.xmax / xScale), 0, image.nColumns); // exclusive
        var rowFrom = (0, lodash_1.clamp)(Math.floor(this.state.boxes.visWorld.ymin / yScale), 0, image.nRows);
        var rowTo = (0, lodash_1.clamp)(Math.ceil(this.state.boxes.visWorld.ymax / yScale), 0, image.nRows); // exclusive
        var canvasImage = this.getCleanCanvasImage();
        for (var iy = rowFrom; iy < rowTo; iy++) {
            var y = this.state.scales.worldToCanvas.y(iy * yScale);
            var yFrom = y + yHalfGap;
            var yTo = y + rectHeight - yHalfGap;
            for (var ix = colFrom; ix < colTo; ix++) {
                var x = this.state.scales.worldToCanvas.x(ix * xScale);
                var xFrom = x + xHalfGap;
                var xTo = x + rectWidth - xHalfGap;
                var color = image_1.Image.getColor(image, ix, iy);
                image_1.Image.addRect(canvasImage, xFrom, yFrom, xTo, yTo, color);
            }
        }
        var imageData = image_1.Image.toImageData(canvasImage, this.getCanvasImageData());
        this.ctx.clearRect(0, 0, scales_1.Box.width(this.state.boxes.canvas), scales_1.Box.height(this.state.boxes.canvas));
        this.ctx.putImageData(imageData, 0, 0);
    };
    /** Adjust the logical size of the canvas to its current DOM size */
    DrawBehavior.prototype.resizeCanvas = function () {
        if (!this.ctx)
            return;
        var width = Math.floor(scales_1.Box.width(this.state.boxes.canvas)); // Canvas context would round it down anyway
        var height = Math.floor(scales_1.Box.height(this.state.boxes.canvas)); // Canvas context would round it down anyway
        if (this.ctx.canvas.width !== width) {
            this.ctx.canvas.width = width;
        }
        if (this.ctx.canvas.height !== height) {
            this.ctx.canvas.height = height;
        }
    };
    /** Return horizontal gap between rectangles, in canvas pixels */
    DrawBehavior.prototype.getXGap = function (colWidthOnCanvas) {
        var _a;
        var gap1 = (0, lodash_1.isNil)(this.params.xGapPixels) ? undefined : this.params.xGapPixels;
        var gap2 = (0, lodash_1.isNil)(this.params.xGapRelative) ? undefined : this.params.xGapRelative * colWidthOnCanvas;
        return (0, lodash_1.clamp)((_a = (0, utils_1.minimum)(gap1, gap2)) !== null && _a !== void 0 ? _a : 0, 0, colWidthOnCanvas);
    };
    /** Return vertical gap between rectangles, in canvas pixels */
    DrawBehavior.prototype.getYGap = function (rowHeightOnCanvas) {
        var _a;
        var gap1 = (0, lodash_1.isNil)(this.params.yGapPixels) ? undefined : this.params.yGapPixels;
        var gap2 = (0, lodash_1.isNil)(this.params.yGapRelative) ? undefined : this.params.yGapRelative * rowHeightOnCanvas;
        return (0, lodash_1.clamp)((_a = (0, utils_1.minimum)(gap1, gap2)) !== null && _a !== void 0 ? _a : 0, 0, rowHeightOnCanvas);
    };
    return DrawBehavior;
}(extension_1.BehaviorBase));
exports.DrawBehavior = DrawBehavior;
/** Adds behavior that draws heatmap data in the canvas element (uses downsampling when the heatmap is zoomed out, to achieve efficient rendering). */
exports.DrawExtension = extension_1.Extension.fromBehaviorClass({
    name: 'builtin.draw',
    defaultParams: exports.DefaultDrawExtensionParams,
    behavior: DrawBehavior,
});
